// ============================================================
// FILE: src/components/Navbar.jsx
// ============================================================
import React, { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    const navLinks = [
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' },
        { name: 'Services', path: '/services' },
        { name: 'Projects', path: '/projects' },
        { name: 'Contact', path: '/contact' },
    ];

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 16);

        handleScroll();

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // ✅ Menu open hone pe ya scroll hone pe navbar white
    const solidNav = isScrolled || isOpen;

    return (
        <nav
            className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${solidNav
                ? 'bg-white border-b border-slate-200 shadow-lg'
                : 'bg-white/90 backdrop-blur-sm border-b border-slate-200'
                }`}
        >
            <div className="container-custom">
                <div className="flex justify-between items-center h-20 sm:h-24">

                    {/* Logo */}
                    <Link to="/" className="flex items-center shrink-0">
                        <img
                            src="/mainlogos.png"
                            alt="Sanwariya Ji logo"
                            className="h-16 sm:h-24 md:h-28 w-auto object-contain"
                        />
                    </Link>

                    {/* Desktop Nav */}
                    <div className="hidden lg:flex items-center space-x-1">
                        {navLinks.map((link) => (
                            <NavLink
                                key={link.path}
                                to={link.path}
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 ${isActive
                                        ? 'bg-[var(--color-accent)] text-white'
                                        : 'text-[var(--color-accent)] hover:bg-[var(--color-accent)]/10'
                                    }`
                                }
                            >
                                {link.name}
                            </NavLink>
                        ))}
                    </div>

                    {/* ✅ Mobile Toggle — white button, theme blue icon */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        aria-label={isOpen ? 'Close menu' : 'Open menu'}
                        className="lg:hidden relative flex h-12 w-12 items-center justify-center rounded-xl bg-white border-2 border-[var(--color-accent)] text-[var(--color-accent)] shadow-lg shadow-black/10 hover:bg-[var(--color-accent)]/10 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
                    >
                        {isOpen ? (
                            <X size={26} strokeWidth={2.5} />
                        ) : (
                            <Menu size={26} strokeWidth={2.5} />
                        )}
                    </button>
                </div>
            </div>

            {/* Mobile Menu — white bg, theme blue text */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="lg:hidden bg-white border-b border-slate-200 overflow-hidden"
                    >
                        <div className="container-custom py-4 flex flex-col space-y-2">
                            {navLinks.map((link) => (
                                <NavLink
                                    key={link.path}
                                    to={link.path}
                                    onClick={() => setIsOpen(false)}
                                    className={({ isActive }) =>
                                        `px-4 py-3 rounded-xl text-base font-semibold transition-all ${isActive
                                            ? 'bg-[var(--color-accent)] text-white shadow-md'
                                            : 'text-[var(--color-accent)] bg-[var(--color-accent)]/10 hover:bg-[var(--color-accent)]/20'
                                        }`
                                    }
                                >
                                    {link.name}
                                </NavLink>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;